import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Calculator,
  Info,
  Loader2,
  MessageSquare,
  Phone,
  Sparkles,
  Facebook,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AU_TRADE_BENCHMARKS, getBenchmarkById } from '@/data/auTradeBenchmarks';
import { estimateMonthlyUsageAUD } from '@/lib/usagePricing';
import { cn } from '@/lib/utils';

const SLIDER_MIN = 0;
const SLIDER_MAX = 500;
const SLIDER_STEP = 25;
const SLIDER_TICKS = Array.from(
  { length: (SLIDER_MAX - SLIDER_MIN) / SLIDER_STEP + 1 },
  (_, idx) => SLIDER_MIN + idx * SLIDER_STEP
);

function formatAud(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Explains which usage band sets the platform fee (matches `usagePricing.js` tiers). */
function platformUsageBandLabel(usageSubtotalAUD) {
  const u = Number(usageSubtotalAUD) || 0;
  if (u < 100) return 'Usage under $100 — platform $30';
  if (u < 200) return '$100–$199 usage — platform $20';
  if (u < 300) return '$200–$299 usage — platform $10';
  return '$300+ usage — no platform fee';
}

export function InteractivePricingCalculator({
  className,
  variant = 'dashboard',
  onGetStarted,
  /** Dashboard /pricing: agents for subscription checkout */
  agents = [],
  selectedAgentId = '',
  onAgentChange,
  isAuthenticated = false,
  subscribeSubmitting = false,
  /** Opens Stripe Checkout for calculator-priced subscription (see createDynamicSubscriptionCheckout). */
  onSubscribeNow,
}) {
  const [industryId, setIndustryId] = useState('plumber');
  const [calls, setCalls] = useState(0);
  const [sms, setSms] = useState(0);
  const [facebook, setFacebook] = useState(100);

  const benchmark = useMemo(() => getBenchmarkById(industryId), [industryId]);

  const estimate = useMemo(
    () =>
      estimateMonthlyUsageAUD({
        calls,
        sms,
        facebookInteractions: facebook,
      }),
    [calls, sms, facebook]
  );

  const jobMid = benchmark.avgSmallJobMidAUD;

  const comparisonParagraph = useMemo(() => {
    const monthly = estimate.totalMonthlyAUD;
    const trade = benchmark.label;
    const leads = benchmark.leadCost;
    const monthlyStr = formatAud(monthly);
    const jobStr = formatAud(jobMid);
    const revenueMultiple =
      monthly > 0 && jobMid > 0 ? jobMid / monthly : 0;
    const monthlyOutweighsSmallJob = monthly > jobMid && jobMid > 0;

    return {
      trade,
      leads,
      monthlyStr,
      jobStr,
      revenueMultiple,
      monthlyOutweighsSmallJob,
    };
  }, [benchmark.label, benchmark.leadCost, estimate.totalMonthlyAUD, jobMid]);

  const calculatorSummary = useMemo(
    () => ({
      industry: {
        id: industryId,
        label: benchmark.label,
      },
      usage: {
        calls,
        sms,
        facebook_interactions: facebook,
      },
      estimate: {
        calls_aud: estimate.callsAUD,
        sms_aud: estimate.smsAUD,
        facebook_aud: estimate.facebookAUD,
        usage_subtotal_aud: estimate.usageSubtotalAUD,
        platform_aud: estimate.platformAUD,
        total_monthly_aud: estimate.totalMonthlyAUD,
      },
      comparison: {
        small_job_reference_aud: jobMid,
        lead_acquisition_cost_range: benchmark.leadCost,
      },
    }),
    [industryId, benchmark.label, benchmark.leadCost, calls, sms, facebook, estimate, jobMid]
  );

  return (
    <div className={cn('space-y-8', className)} data-testid="interactive-pricing-calculator">
      <div className="grid gap-8 lg:grid-cols-2 lg:items-stretch">
        <Card className="border-slate-200 shadow-sm h-full flex flex-col">
          <CardHeader>
            <div className="flex items-center gap-2 text-teal-700">
              <Calculator className="w-5 h-5" />
              <CardTitle className="text-xl">Estimate your usage</CardTitle>
            </div>
            <CardDescription>
              Use the sliders for a rough monthly total in AUD. Platform fee depends on how much usage you run - higher
              usage lowers the platform portion (see breakdown).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8 flex-1">
            <div className="space-y-2">
              <Label htmlFor="industry-select" className="text-slate-700">
                Your industry <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-slate-500">
                We use this to compare your estimate with typical job values in Australia (2025–26 benchmarks).
              </p>
              <Select value={industryId} onValueChange={setIndustryId}>
                <SelectTrigger id="industry-select" className="w-full bg-white" data-testid="pricing-industry-select">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {AU_TRADE_BENCHMARKS.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {variant === 'dashboard' && typeof onSubscribeNow === 'function' && (
              <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/90 p-4">
                <Label htmlFor="pricing-subscribe-agent" className="text-slate-700">
                  Agent <span className="text-red-500">*</span> <span className="font-normal">(required to subscribe)</span>
                </Label>
                <select
                  id="pricing-subscribe-agent"
                  className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                  value={selectedAgentId}
                  onChange={(e) => onAgentChange?.(e.target.value)}
                  disabled={!isAuthenticated || agents.length === 0}
                  data-testid="pricing-subscribe-agent-select"
                >
                  {agents.length === 0 ? (
                    <option value="">
                      {isAuthenticated ? 'No agents — create one from the dashboard' : 'Sign in to load your agents'}
                    </option>
                  ) : (
                    agents.map((agent) => (
                      <option key={agent.agent_id} value={agent.agent_id}>
                        {agent.name || agent.agent_id}
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-center gap-2 text-slate-700">
                <Phone className="w-4 h-4 text-teal-600 shrink-0" />
                <Label htmlFor="pricing-slider-calls" className="font-medium">
                  Calls
                </Label>
              </div>
              <div className="space-y-2">
                <Slider
                  id="pricing-slider-calls"
                  value={[calls]}
                  min={SLIDER_MIN}
                  max={SLIDER_MAX}
                  step={SLIDER_STEP}
                  onValueChange={(v) => setCalls(v[0] ?? 0)}
                  className="py-1 min-w-0"
                />
                <div className="flex items-center justify-between px-1" aria-hidden>
                  {SLIDER_TICKS.map((tick) => (
                    <span
                      key={`calls-tick-${tick}`}
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        tick <= calls ? 'bg-teal-500' : 'bg-slate-300'
                      )}
                    />
                  ))}
                </div>
                <p className="text-xs text-slate-500 tabular-nums">
                  {calls} minutes
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2 text-slate-700">
                <MessageSquare className="w-4 h-4 text-teal-600 shrink-0" />
                <Label htmlFor="pricing-slider-sms" className="font-medium">
                  SMS
                </Label>
              </div>
              <div className="space-y-2">
                <Slider
                  id="pricing-slider-sms"
                  value={[sms]}
                  min={SLIDER_MIN}
                  max={SLIDER_MAX}
                  step={SLIDER_STEP}
                  onValueChange={(v) => setSms(v[0] ?? 0)}
                  className="py-1 min-w-0"
                />
                <div className="flex items-center justify-between px-1" aria-hidden>
                  {SLIDER_TICKS.map((tick) => (
                    <span
                      key={`sms-tick-${tick}`}
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        tick <= sms ? 'bg-teal-500' : 'bg-slate-300'
                      )}
                    />
                  ))}
                </div>
                <p className="text-xs text-slate-500 tabular-nums">{sms}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2 text-slate-700">
                <Facebook className="w-4 h-4 text-teal-600 shrink-0" />
                <Label htmlFor="pricing-slider-facebook" className="font-medium">
                  Facebook interactions
                </Label>
              </div>
              <div className="space-y-2">
                <Slider
                  id="pricing-slider-facebook"
                  value={[facebook]}
                  min={SLIDER_MIN}
                  max={SLIDER_MAX}
                  step={SLIDER_STEP}
                  onValueChange={(v) => setFacebook(v[0] ?? 0)}
                  className="py-1 min-w-0"
                />
                <div className="flex items-center justify-between px-1" aria-hidden>
                  {SLIDER_TICKS.map((tick) => (
                    <span
                      key={`facebook-tick-${tick}`}
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        tick <= facebook ? 'bg-teal-500' : 'bg-slate-300'
                      )}
                    />
                  ))}
                </div>
                <p className="text-xs text-slate-500 tabular-nums">{facebook}</p>
              </div>
              <p className="text-xs text-slate-500">
                The <strong>first 100</strong> Facebook interactions are <strong>free</strong>. Interactions{' '}
                <strong>101–200</strong> are <strong>$10 AUD</strong>. From <strong>201–500</strong>, each block of up to{' '}
                <strong>100</strong> interactions adds <strong>$30 AUD</strong>.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-teal-200 bg-gradient-to-br from-teal-50/80 to-white shadow-md h-full flex flex-col">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Estimated monthly total</CardTitle>
            <CardDescription>
              Total = usage (call minutes, SMS, Facebook) + tiered platform fee. Platform fee drops as monthly usage
              crosses $100, $200, and $300.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 flex-1 flex flex-col">
            <p className="text-4xl font-bold tracking-tight text-teal-800 tabular-nums">
              {formatAud(estimate.totalMonthlyAUD)}
            </p>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
              <p className="text-sm text-emerald-900">
                Protecting even one extra <strong>{comparisonParagraph.trade}</strong> booking can often outweigh this
                monthly estimate.
              </p>
            </div>
            <ul className="space-y-2 text-sm border-t border-teal-100 pt-4">
              <li className="flex justify-between gap-4">
                <span className="text-slate-600">
                  Calls (minutes) ({calls})
                </span>
                <span className="font-medium tabular-nums">{formatAud(estimate.callsAUD)}</span>
              </li>
              <li className="flex justify-between gap-4">
                <span className="text-slate-600">SMS ({sms})</span>
                <span className="font-medium tabular-nums">{formatAud(estimate.smsAUD)}</span>
              </li>
              <li className="flex justify-between gap-4">
                <span className="text-slate-600">Facebook</span>
                <span className="font-medium tabular-nums">{formatAud(estimate.facebookAUD)}</span>
              </li>
              <li className="flex justify-between gap-4 border-t border-teal-100 pt-2 text-slate-500">
                <span>Usage subtotal</span>
                <span className="font-medium tabular-nums">{formatAud(estimate.usageSubtotalAUD)}</span>
              </li>
              <li className="flex justify-between gap-4 items-start">
                <span className="text-slate-600">
                  Platform
                  <span className="block text-xs font-normal text-slate-400 mt-1 leading-snug">
                    {platformUsageBandLabel(estimate.usageSubtotalAUD)}
                  </span>
                </span>
                <span className="font-medium tabular-nums shrink-0">{formatAud(estimate.platformAUD)}</span>
              </li>
            </ul>
            <p className="flex items-start gap-2 text-xs text-slate-500">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              {variant === 'marketing'
                ? 'Illustrative estimate only — we confirm details when you get started with our team.'
                : 'Illustrative estimate only — subscription and checkout pricing follow your selected plan at purchase.'}
            </p>

            <div className="rounded-xl border border-teal-200/80 bg-white/70 p-4 flex-1 min-h-[8rem] flex flex-col">
              <div className="flex items-center gap-2 text-slate-900 mb-3">
                <Sparkles className="w-5 h-5 text-amber-500 shrink-0" />
                <span className="font-semibold text-base leading-snug">
                  Why <span className="text-teal-700">Desqly</span> for {comparisonParagraph.trade}?
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Indicative Australian trade figures — not financial advice.
              </p>
              <p className="text-sm text-slate-700 leading-relaxed flex-1">
                <strong className="text-slate-900">Desqly</strong> answers calls and messages around the clock so you
                don&apos;t <strong>leave high-value revenue on the table</strong> for a relatively{' '}
                <strong>low monthly outlay</strong>. For <strong>{comparisonParagraph.trade}</strong>, a routine small job
                is often around <strong>{comparisonParagraph.jobStr}</strong> — while your estimate here is{' '}
                <strong>{comparisonParagraph.monthlyStr}</strong>
                {comparisonParagraph.monthlyOutweighsSmallJob ? (
                  <>
                    . At this usage level the estimate exceeds that small-job benchmark; in practice,{' '}
                    <strong>one larger job</strong> in your trade still often <strong>covers many months</strong> of this
                    kind of coverage — and unanswered enquiries still mean <strong>forgone revenue</strong>.
                  </>
                ) : comparisonParagraph.revenueMultiple >= 1 ? (
                  <>
                    , so <strong>one</strong> extra job won from faster answering is roughly{' '}
                    <strong>{comparisonParagraph.revenueMultiple.toFixed(1)}×</strong> that spend.{' '}
                    <strong>Missing one booking</strong> is the same order of <strong>lost revenue</strong>.
                  </>
                ) : null}{' '}
                <strong>Lead acquisition</strong> in your sector commonly runs{' '}
                <strong>{comparisonParagraph.leads}</strong> per lead; Desqly helps you{' '}
                <strong>defend that investment</strong> so paid leads don&apos;t go cold when the phone rings.
              </p>
              <p className="text-xs text-slate-500 mt-3">Benchmarks are indicative ranges for comparison only.</p>
            </div>

            <div className="pt-2 mt-auto">
              {variant === 'marketing' && typeof onGetStarted === 'function' ? (
                <Button
                  type="button"
                  className="w-full bg-teal-600 hover:bg-teal-700 h-11"
                  onClick={() => onGetStarted(calculatorSummary)}
                  data-testid="pricing-calculator-get-started"
                >
                  Get started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : variant === 'dashboard' && typeof onSubscribeNow === 'function' ? (
                <Button
                  type="button"
                  className="w-full bg-teal-600 hover:bg-teal-700 h-11"
                  disabled={subscribeSubmitting}
                  onClick={() =>
                    onSubscribeNow({
                      calls,
                      sms,
                      facebookInteractions: facebook,
                      industryId,
                    })
                  }
                  data-testid="pricing-subscribe-now"
                >
                  {subscribeSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Subscribe now
                  {!subscribeSubmitting && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              ) : (
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 h-11">
                  <Link to="/buy-minutes?tab=plans">
                    Subscribe now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default InteractivePricingCalculator;
